import {Component, computed, effect, inject, OnInit, Signal, signal, untracked, WritableSignal} from '@angular/core';
import {SelectItem, SelectItemGroup} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {AlertService, isNotNullOrUndefined} from 'sigale-angular';
import {ReservationService} from '../shared/services/reservation.service';
import {EvenementService} from '../shared/services/evenement.service';
import {GroupeDeTravailService} from '../gestion-equipe/onglets-gestion-equipe/groupe-de-travail/service/groupe-de-travail.service';
import {EquipeService} from '../gestion-equipe/onglets-gestion-equipe/equipe/service/equipe.service';
import {GroupeDeTravailDto} from '../gestion-equipe/onglets-gestion-equipe/groupe-de-travail/model/groupe-de-travail.dto';
import {EquipeDto} from '../gestion-equipe/onglets-gestion-equipe/equipe/model/equipe.dto';
import {forkJoin} from 'rxjs';
import {AuthService} from '../shared/services/auth-service.service';
import {UtilisateurDto} from '../shared/dto/utilisateur.dto';
import {DateUtilsService} from '../shared/services/date-utils.service';
import {finalize} from 'rxjs/operators';
import {TypeEvenement} from '../shared/models/type-evenement.model';
import {EvenementPlanning} from './model/evenement-planning.model';
import {Semaine} from '../shared/components/calendrier/model/semaine.model';
import {EvenementDto} from '../shared/dto/evenement.dto';
import {TypeChangementDate} from '../shared/components/calendrier/model/type-changment-date.model';
import {DateService} from '@sigale-angular/src/services/date.service';
import {UtilisateurAvecBureauDto} from './model/utilisateur-avec-bureau.dto';
import {UtilisateurSigalettService} from '../shared/services/utilisateur-sigalett.service';
import {StorageService} from '../shared/services/storage.service';
import {UtilisateurSigalett} from '../shared/models/utilisateur-sigalett.model';
import {UtilisateurOption} from './model/utilisateur-option.model';
import {EquipeGroupeDeTravailItem} from './model/equipe-groupe-de-travail-item.model';
import {TypeFiltre} from './model/type-filtre';

@Component({
	selector: 'app-planning',
	templateUrl: './planning.component.html',
	styleUrl: './planning.component.scss'
})
export class PlanningComponent implements OnInit {
	readonly NOM_ALERTE_PLANNING = 'alertePlanning';

	dateActuelle = signal<Date>(new Date());
	semaine = signal<Semaine | null>(null);
	loading: WritableSignal<boolean> = signal(false);
	afficherWeekend = signal(false);

	// Autocomplete utilisateurs administrateur
	utilisateursSuggestions = signal<UtilisateurDto[]>([]);
	utilisateurAAjouter = signal<UtilisateurDto | null>(null);
	loadingAjouterUtilisateur = signal<boolean>(false);
	utilisateursSelectionnesAutocompleteAdmin = signal<UtilisateurDto[]>([]);

	groupesDeTravailSelectionnes = signal<GroupeDeTravailDto[]>([]);
	equipesSelectionnes = signal<EquipeDto[]>([]);
	utilisateursSelectionnes = signal<UtilisateurOption[]>([]);
	utilisateursOptions = signal<UtilisateurOption[]>([]);

	evenements = signal<EvenementDto[]>([]);
	utilisateursAvecBureau = signal<UtilisateurAvecBureauDto[]>([]);

	evenementPlanningParDate: Signal<Map<string, EvenementPlanning[]>> = computed((): Map<string, EvenementPlanning[]> => {
		const groupesDeTravailSelectionnes = this.groupesDeTravailSelectionnes();
		const equipesSelectionnes = this.equipesSelectionnes();
		const utilisateurs = this.utilisateursSelectionnes();
		const utilisateursSelectionnesAutocompleteAdmin = this.utilisateursSelectionnesAutocompleteAdmin();

		const loginsUtilisateurs: string[] = [];

		// Si on a sélectionné des utilisateurs alors, on filtre seulement avec ces derniers
		if (utilisateurs.length > 0) {
			loginsUtilisateurs.push(...utilisateurs.map(utilisateur => utilisateur.login));
		}
		else {
			loginsUtilisateurs.push(...groupesDeTravailSelectionnes.map(groupeDeTravail => groupeDeTravail.membres).flat().map(utilisateur => utilisateur.login));
			loginsUtilisateurs.push(...equipesSelectionnes.map(equipe => equipe.membres).flat().map(utilisateur => utilisateur.login));
			loginsUtilisateurs.push(...utilisateursSelectionnesAutocompleteAdmin.map(utilisateur => utilisateur.login));
		}

		const evenements = this.evenements().filter(evenement =>
			loginsUtilisateurs.some(login => login === evenement.demandeur.login)
		);

		const utilisateursAvecBureau = this.utilisateursAvecBureau().filter(reservation =>
			loginsUtilisateurs.some(login => login === reservation.occupant.login)
		);

		return this.construireMapEvenementPlanningParDate(evenements, utilisateursAvecBureau);
	});

	equipesEtGroupesDeTravailItemsGroup = signal<SelectItemGroup[]>([]);

	equipesGroupesDeTravailSelectionnes: EquipeGroupeDeTravailItem[] = [];
	groupesDeTravail: GroupeDeTravailDto[] = [];
	equipes: EquipeDto[] = [];
	utilisateurs: UtilisateurDto[] = [];
	utilisateurConnecte: UtilisateurSigalett;
	login: string;
	locale: string;

	private reservationService = inject(ReservationService);
	private evenementService = inject(EvenementService);
	private groupeDeTravailService = inject(GroupeDeTravailService);
	private equipeService = inject(EquipeService);
	private utilisateurService = inject(UtilisateurSigalettService);
	private authService = inject(AuthService);
	private alertService = inject(AlertService);
	private dateUtilsService = inject(DateUtilsService);
	private dateService = inject(DateService);
	private storageService = inject(StorageService);
	private translateService = inject(TranslateService);

	constructor() {
		effect(() => {
			const groupesDeTravailSelectionnes = this.groupesDeTravailSelectionnes();
			const equipesSelectionnes = this.equipesSelectionnes();
			const utilisateursSelectionnesAutocomplete = this.utilisateursSelectionnesAutocompleteAdmin();

			untracked(() => {
				const utilisateursParLogin = new Map<string, UtilisateurOption>();

				const utilisateursGroupeDeTravailEtEquipe = groupesDeTravailSelectionnes.map(groupeDeTravail => groupeDeTravail.membres).flat();
				utilisateursGroupeDeTravailEtEquipe.push(...equipesSelectionnes.map(equipe => equipe.membres).flat());

				utilisateursGroupeDeTravailEtEquipe.push(...utilisateursSelectionnesAutocomplete);

				utilisateursGroupeDeTravailEtEquipe.forEach(utilisateur => {
					if (!utilisateursParLogin.has(utilisateur.login)) {
						utilisateursParLogin.set(utilisateur.login, {
							login: utilisateur.login,
							prenomNom: `${utilisateur.prenom} ${utilisateur.nom}`
						});
					}
				});

				const utilisateursOptions = [...utilisateursParLogin.values()].sort((a, b) => a.prenomNom.localeCompare(b.prenomNom));

				this.utilisateursOptions.set(utilisateursOptions);
				this.utilisateursSelectionnes.set([]);
			});
		});
	}

	ngOnInit(): void {
		this.utilisateurConnecte = this.authService.getUtilisateurConnecte();
		this.login = this.utilisateurConnecte.login;
		this.locale = this.utilisateurConnecte.langue;

		this.loading.set(true);

		this.utilisateurService.rechercherPreferencesUtilisateur(this.authService.getUtilisateurConnecte().login)
			.pipe(finalize(() => {
				this.modifierJoursDeLaSemaineAvecDateActuelle();
				this.recupererGroupesDeTravailEquipes();
			}))
			.subscribe({
				next: preferencesUtilisateurDto => {
					this.afficherWeekend.set(preferencesUtilisateurDto.afficherWeekend);
				},
				error: () => {
					// tant pis
				}
			});
	}

	/**
	 * Renvoi le prénom et le nom pour l'autocomplete accessible aux administrateurs.
	 * @param utilisateur l'utilisateur concerné.
	 */
	recupererPrenomNomSuggestionAutomcomplete(utilisateur: UtilisateurDto): string {
		return `${utilisateur.prenom} ${utilisateur.nom}`;
	}

	/**
	 * Met à jour la liste d'équipe et de groupe de travail pour le dropdown.
	 */
	mettreAJourEquipesEtGroupesDeTravailItemsGroup(): void {
		const equipesItems: SelectItem[] = this.equipes
			.filter(equipe => !this.equipesSelectionnes().some(equipeSelectionne => equipeSelectionne.id === equipe.id))
			.map(equipe => {
				return {label: equipe.nom, value: new EquipeGroupeDeTravailItem(TypeFiltre.EQUIPE, equipe.id)};
			});
		const groupesDeTravailItems: SelectItem[] = this.groupesDeTravail
			.filter(groupeDeTravail => !this.groupesDeTravailSelectionnes().some(groupeSelectionne => groupeSelectionne.id === groupeDeTravail.id))
			.map(groupeDeTravail => {
				return {label: groupeDeTravail.nom, value: new EquipeGroupeDeTravailItem(TypeFiltre.GROUPE_DE_TRAVAIL, groupeDeTravail.id)};
			});

		const equipesEtGroupesDeTravailItemsGroup: SelectItemGroup[] = [];

		if (equipesItems.length > 0) {
			equipesEtGroupesDeTravailItemsGroup.push({
				label: this.translateService.instant('PLANNING.FILTRES.LABEL_EQUIPES'),
				items: equipesItems
			});
		}

		if (groupesDeTravailItems.length > 0) {
			equipesEtGroupesDeTravailItemsGroup.push({
				label: this.translateService.instant('PLANNING.FILTRES.LABEL_GROUPES_DE_TRAVAIL'),
				items: groupesDeTravailItems
			});
		}

		this.equipesEtGroupesDeTravailItemsGroup.set(equipesEtGroupesDeTravailItemsGroup);
	}

	/**
	 * Retire l'équipe passée en paramètre des équipes sélectionnées.
	 * @param equipe l'équipe concernée.
	 */
	onRemoveFiltreEquipe(equipe: EquipeDto): void {
		this.equipesSelectionnes.update(equipesSelectionnees => equipesSelectionnees.filter(equipeSelectionnee => equipeSelectionnee.id !== equipe.id));
		this.storageService.planningEquipesSelectionnees = this.equipesSelectionnes().map(equipe => equipe.id);
		this.mettreAJourEquipesEtGroupesDeTravailItemsGroup();
	}

	/**
	 * Retire le groupe de travail passé en paramètre des groupes de travail sélectionnés.
	 * @param groupeDeTravail le groupe de travail concerné.
	 */
	onRemoveFiltreGroupeDeTravail(groupeDeTravail: GroupeDeTravailDto): void {
		this.groupesDeTravailSelectionnes.update(groupesSelectionnes => groupesSelectionnes.filter(groupeSelectionne => groupeSelectionne.id !== groupeDeTravail.id));
		this.storageService.planningGroupesDeTravailSelectionnes = this.groupesDeTravailSelectionnes().map(groupe => groupe.id);
		this.mettreAJourEquipesEtGroupesDeTravailItemsGroup();
	}

	recupererEquipe(id: number): EquipeDto | undefined {
		return this.equipes.find(equipe => equipe.id === id);
	}

	recupererGroupeDeTravail(id: number): GroupeDeTravailDto | undefined {
		return this.groupesDeTravail.find(groupeDeTravail => groupeDeTravail.id === id);
	}

	/**
	 * Ajoute les équipes et groupes de travail sélectionnés dans le dropdown aux équipes et groupes de travail sélectionnés pour le planning.
	 */
	onClickAjouterEquipesEtGroupesDeTravailSelectionnes(): void {
		this.equipesGroupesDeTravailSelectionnes
			.forEach(equipeGroupeItem => {
				const elementId = equipeGroupeItem.id;

				if (equipeGroupeItem.typeFiltre === TypeFiltre.EQUIPE) {
					const equipe = this.recupererEquipe(elementId);
					if (isNotNullOrUndefined(equipe) && !this.equipesSelectionnes().some(equipeSelectionnee => equipeSelectionnee.id === elementId)) {
						this.equipesSelectionnes.update(equipesSelectionnees => [...equipesSelectionnees, equipe]);
						this.storageService.planningEquipesSelectionnees = this.equipesSelectionnes().map(equipe => equipe.id);
					}
				}
				else {
					const groupeDeTravail = this.recupererGroupeDeTravail(elementId);
					if (isNotNullOrUndefined(groupeDeTravail) && !this.groupesDeTravailSelectionnes().some(groupeDeTravailSelectionne => groupeDeTravailSelectionne.id === elementId)) {
						this.groupesDeTravailSelectionnes.update(groupesSelectionnes => [...groupesSelectionnes, groupeDeTravail]);
						this.storageService.planningGroupesDeTravailSelectionnes = this.groupesDeTravailSelectionnes().map(groupe => groupe.id);
					}
				}
			});

		this.equipesGroupesDeTravailSelectionnes.length = 0;
		this.mettreAJourEquipesEtGroupesDeTravailItemsGroup();
	}

	/**
	 * Récupère les groupes de travail et les équipes selon le rôle de l'utilisateur connecté.
	 */
	recupererGroupesDeTravailEquipes(): void {
		forkJoin([
			this.groupeDeTravailService.recupererGroupesDeTravailSelonRoleUtilisateur(this.login),
			this.equipeService.recupererEquipesSelonRoleUtilisateur(this.login)])
			.subscribe({
				next: ([groupesDeTravail, equipes]) => {
					this.groupesDeTravail = groupesDeTravail;
					this.equipes = equipes;

					this.utilisateurs = groupesDeTravail.map(groupeDeTravail => groupeDeTravail.membres).flat();
					this.utilisateurs.push(...equipes.map(equipe => equipe.membres).flat());

					const equipesIdsSelectionneesStorage = this.storageService.planningEquipesSelectionnees;
					const groupesDeTravailIdsSelectionneesStorage = this.storageService.planningGroupesDeTravailSelectionnes;

					const equipesSelectionnees: EquipeDto[] = [];
					const groupesDeTravailSelectionnes: GroupeDeTravailDto[] = [];

					if (isNotNullOrUndefined(equipesIdsSelectionneesStorage)) {
						equipesSelectionnees.push(...this.equipes.filter(equipe => equipesIdsSelectionneesStorage.includes(equipe.id)));
					}
					else {
						const equipeResponsable = this.equipes.find(equipe => equipe.loginResponsable === this.login);

						if (isNotNullOrUndefined(equipeResponsable)) {
							equipesSelectionnees.push(equipeResponsable);
						}
						else {
							equipesSelectionnees.push(...this.equipes);
						}
					}

					if (isNotNullOrUndefined(groupesDeTravailIdsSelectionneesStorage)) {
						groupesDeTravailSelectionnes.push(...this.groupesDeTravail.filter(groupeDeTravail => groupesDeTravailIdsSelectionneesStorage.includes(groupeDeTravail.id)));
					}
					else {
						groupesDeTravailSelectionnes.push(...this.groupesDeTravail);
					}

					this.equipesSelectionnes.set(equipesSelectionnees);
					this.groupesDeTravailSelectionnes.set(groupesDeTravailSelectionnes);

					this.mettreAJourEquipesEtGroupesDeTravailItemsGroup();

					this.recupererEvenementsEtReservationsUtilisateurs();
				},
				error: (error) => {
					this.loading.set(false);
					this.alertService.clear(this.NOM_ALERTE_PLANNING);
					this.alertService.error(error.error, this.NOM_ALERTE_PLANNING);
				},
			});
	}

	/**
	 * Récupère les évènements et les réservations de bureau des utilisateurs sélectionnés.
	 */
	recupererEvenementsEtReservationsUtilisateurs(): void {
		if (!this.loading()) {
			this.loading.set(true);
		}

		const dateDebutSemaine = this.semaine()?.jours[0];
		const dateFinSemaine = this.semaine()?.jours[this.semaine()!.jours.length - 1];

		const dateDebut = this.dateUtilsService.formaterDateEnStringPourEnvoiBack(dateDebutSemaine, this.locale);
		const dateFin = this.dateUtilsService.formaterDateEnStringPourEnvoiBack(dateFinSemaine, this.locale);

		const loginsUtilisateurs = this.utilisateurs.map(utilisateur => utilisateur.login);

		forkJoin([
			this.evenementService.recupererEvenementsPourLoginDemandeurEtPeriode(loginsUtilisateurs, dateDebut, dateFin),
			this.reservationService.recupererReservationPourPeriodeEtOccupantsBureauPreferentielInclus(dateDebut, dateFin, loginsUtilisateurs)])
			.pipe(finalize(() => this.loading.set(false)))
			.subscribe({
				next: ([evenements, utilisateursAvecBureau]) => {
					this.evenements.set(evenements);
					this.utilisateursAvecBureau.set(utilisateursAvecBureau);
				},
				error: (error) => {
					this.alertService.clear(this.NOM_ALERTE_PLANNING);
					this.alertService.error(error.error, this.NOM_ALERTE_PLANNING);
				},
			});
	}

	/**
	 * Récupère les évènements et les réservations de bureau pour l'utilisateur ajouté via l'autocomplete administrateur.
	 * @param utilisateurAutocompleteAdmin l'utilisateur ajouté via l'autocomplete.
	 */
	recupererEvenementsUtilisateurAutoCompleteAdmin(utilisateurAutocompleteAdmin: UtilisateurDto): void {
		const dateDebutSemaine = this.semaine()?.jours[0];
		const dateFinSemaine = this.semaine()?.jours[this.semaine()!.jours.length - 1];

		const dateDebut = this.dateUtilsService.formaterDateEnStringPourEnvoiBack(dateDebutSemaine, this.locale);
		const dateFin = this.dateUtilsService.formaterDateEnStringPourEnvoiBack(dateFinSemaine, this.locale);

		forkJoin([
			this.evenementService.recupererEvenementsPourLoginDemandeurEtPeriode([utilisateurAutocompleteAdmin.login], dateDebut, dateFin),
			this.reservationService.recupererReservationPourPeriodeEtOccupantsBureauPreferentielInclus(dateDebut, dateFin, [utilisateurAutocompleteAdmin.login])])
			.pipe(finalize(() => this.loading.set(false)))
			.subscribe({
				next: ([evenementsUtilisateurAutocomplete, utilisateursAvecBureauUtilisateurAutocomplete]) => {
					this.evenements.update(evenements => [...evenements, ...evenementsUtilisateurAutocomplete]);
					this.utilisateursAvecBureau.update(utilisateursAvecBureau => [...utilisateursAvecBureau, ...utilisateursAvecBureauUtilisateurAutocomplete]);

					this.utilisateurs.push(utilisateurAutocompleteAdmin);

					this.utilisateursSelectionnesAutocompleteAdmin.update(utilisateurs => [...utilisateurs, utilisateurAutocompleteAdmin]);
				},
				error: (error) => {
					this.alertService.clear(this.NOM_ALERTE_PLANNING);
					this.alertService.error(error.error, this.NOM_ALERTE_PLANNING);
				},
			});
	}

	/**
	 * Construit une map des évènements planning par date.
	 * @param evenements les évènements concernés.
	 * @param utilisateursAvecBureau les utilisateurs avec un bureau sur la journée.
	 */
	construireMapEvenementPlanningParDate(evenements: EvenementDto[], utilisateursAvecBureau: UtilisateurAvecBureauDto[]): Map<string, EvenementPlanning[]> {
		const evenementsPlanningParDate = new Map<string, EvenementPlanning[]>();

		// créer les élements de planning pour les réservations de bureau
		utilisateursAvecBureau.forEach(utilisateurAvecBureau => {
			const cle = this.dateUtilsService.formaterDateEnCle(new Date(utilisateurAvecBureau.date));
			if (!evenementsPlanningParDate.has(cle)) {
				evenementsPlanningParDate.set(cle, []);
			}

			const prenomNomUtilisateur = utilisateurAvecBureau.occupant.prenom + ' ' + utilisateurAvecBureau.occupant.nom;

			evenementsPlanningParDate.get(cle).push(new EvenementPlanning(
				TypeEvenement.BUREAU,
				utilisateurAvecBureau.date,
				utilisateurAvecBureau.duree,
				prenomNomUtilisateur,
				utilisateurAvecBureau.site
			));
		});

		// créer les élements de planning pour les évènements qui ne sont pas des réservations de bureau
		evenements.forEach(evenement => {
			const cle = this.dateUtilsService.formaterDateEnCle(new Date(evenement.dateEvenement));

			const prenomNomUtilisateur = evenement.demandeur.prenom + ' ' + evenement.demandeur.nom;

			let typeEvenement: TypeEvenement;

			switch (evenement.typeEvenement.code) {
				case TypeEvenement.TELETRAVAIL:
					typeEvenement = TypeEvenement.TELETRAVAIL;
					break;
				case TypeEvenement.CONGE:
					typeEvenement = TypeEvenement.CONGE;
					break;
				case TypeEvenement.TRAVAILLE_AILLEURS:
					typeEvenement = TypeEvenement.TRAVAILLE_AILLEURS;
					break;
			}

			if (!evenementsPlanningParDate.has(cle)) {
				evenementsPlanningParDate.set(cle, []);
			}

			evenementsPlanningParDate.get(cle).push(new EvenementPlanning(
				typeEvenement,
				evenement.dateEvenement,
				evenement.duree,
				prenomNomUtilisateur,
			));
		});

		return evenementsPlanningParDate;
	}

	/**
	 * Modifie les jours affichés dans le planning.
	 */
	modifierJoursDeLaSemaineAvecDateActuelle(): void {
		this.semaine.set(new Semaine(this.obtenirJoursSemaine(this.dateActuelle())));
	}

	/**
	 * Récupère les jours de la semaine selon si on affiche les weekends ou pas.
	 * @param date
	 */
	obtenirJoursSemaine(date: Date): Date[] {
		const jourDebutSemaine = 1; // Lundi

		// Obtenir le jour de la semaine (0 = dimanche, 1 = lundi, etc.)
		const jourSemaine = date.getDay();
		const premierJourSemaine = jourSemaine === 0 ? 7 : jourSemaine;

		// Calculer le lundi de cette semaine
		const lundi = new Date(date);
		lundi.setDate(date.getDate() - (premierJourSemaine - jourDebutSemaine));

		const jours: Date[] = [];

		const borneMax = this.afficherWeekend() ? 7 : 5;

		// Générer les jours de lundi à vendredi ou dimanche suivant le paramétrage
		for (let i = 0; i < borneMax; i++) {
			const jour = new Date(lundi);
			jour.setDate(lundi.getDate() + i);
			jours.push(jour);
		}

		return jours;
	}

	/**
	 * Récupère les évènements planning pour une date et un type d'évènement donné.
	 * @param date la date.
	 * @param typeEvenement le type d'évènement.
	 * @param site le site (optionnel).
	 */
	recupererEvenementPlanningPourDateEtTypeEvenement(date: Date, typeEvenement: TypeEvenement, site?: string): EvenementPlanning[] {
		const dateEnString = this.dateUtilsService.formaterDateEnCle(date);

		if (!this.evenementPlanningParDate().has(dateEnString)) {
			return [];
		}

		return this.evenementPlanningParDate().get(dateEnString)
			.filter(evenementPlanning => evenementPlanning.typeEvenement === typeEvenement)
			.filter(evenementPlanning => site == null || evenementPlanning.siteAAfficher() === site)
			.sort((a, b) => a.prenomNomUtilisateur.localeCompare(b.prenomNomUtilisateur));
	}

	/**
	 * Vérifie si la journée possède au moins un évènement planning.
	 * @param date la date que l'on souhaite vérifier.
	 */
	estJourneePossedeEvenementPlanning(date: Date): boolean {
		const cle = this.dateUtilsService.formaterDateEnCle(date);
		return this.evenementPlanningParDate().has(cle) && this.evenementPlanningParDate().get(cle).length > 0;
	}

	/**
	 * Vérifie si la journée possède au moins un évènement planning pour un type d'évènement.
	 * @param date la date.
	 * @param typeEvenement le type d'évènement.
	 */
	estJourneePossedeTypeEvenementPlanning(date: Date, typeEvenement: TypeEvenement): boolean {
		const cle = this.dateUtilsService.formaterDateEnCle(date);
		return this.estJourneePossedeEvenementPlanning(date) &&
			this.evenementPlanningParDate().get(cle).some(evenementPlanning => evenementPlanning.typeEvenement === typeEvenement);
	}

	/**
	 * Récupère les zones réservées pour une date donnée.
	 * @param date la date concernée.
	 */
	recupererSitesReservees(date: Date): string[] {
		const ilotsReserves: string[] = [];
		if (this.estJourneePossedeTypeEvenementPlanning(date, TypeEvenement.BUREAU)) {
			this.recupererEvenementPlanningPourDateEtTypeEvenement(date, TypeEvenement.BUREAU).forEach(evenementPlanning => {
				if (!ilotsReserves.includes(evenementPlanning.siteAAfficher())) {
					ilotsReserves.push(evenementPlanning.siteAAfficher());
				}
			})
		}
		return ilotsReserves.sort((a, b) => a.localeCompare(b));
	}

	/**
	 * Change la semaine dans le calendrier et récupère les évènements/réservations.
	 */
	changerDeSemaine(nouvelleDate: Date): void {
		this.dateActuelle.set(nouvelleDate);
		this.modifierJoursDeLaSemaineAvecDateActuelle();
		if (this.utilisateurs.length > 0) {
			this.recupererEvenementsEtReservationsUtilisateurs();
		}
	}

	/**
	 * Récupère le jour de la semaine en lettre.
	 * @param jour le jour concerné.
	 */
	recupererJourSemaine(jour: Date): string {
		const jourSemaineLettre = this.dateUtilsService.recupererJourSemaineTraduit(jour.getDay());
		return `${jourSemaineLettre} ${jour.getDate()}`;
	}

	/**
	 * Récupère les types d'évènements à afficher dans le planning.
	 */
	recupererTypesEvenements(): TypeEvenement[] {
		return [TypeEvenement.BUREAU, TypeEvenement.TELETRAVAIL, TypeEvenement.TRAVAILLE_AILLEURS, TypeEvenement.CONGE,];
	}

	/**
	 * Renseigne une classe CSS si le jour est le jour courant.
	 * @param jour le jour.
	 */
	classeJourCourant(jour: Date): string {
		return this.dateService.sontDatesMemeJour(new Date(), jour) ? 'jour-courant' : '';
	}

	/**
	 * Recherche les utilisateurs pour l'autocomplete administrateur.
	 * @param $event les infos de l'utilisateur renseigné dans l'autocomplete.
	 */
	rechercherUtilisateurs($event: { originalEvent: InputEvent, query: string }): void {
		if (isNotNullOrUndefined($event.query) && $event.query !== '') {
			this.utilisateurService.rechercherUtilisateursPourAutocomplete($event.query)
				.subscribe({
					next: (utilisateurs) => {
						this.utilisateursSuggestions.set(utilisateurs);
					},
					error: (error) => {
						this.alertService.clear(this.NOM_ALERTE_PLANNING);
						this.alertService.error(error.error, this.NOM_ALERTE_PLANNING);
					},
				});
		}
	}

	/**
	 * Ajouter l'utilisateur récupéré par l'autocomplete depuis le filtre administrateur.
	 */
	ajouterUtilisateurAutoCompleteAdministrateur(): void {
		const utilisateurAAjouter = this.utilisateurAAjouter();
		const utilisateurDejaExistant = this.utilisateurs.find(utilisateur => utilisateur.login === utilisateurAAjouter.login);

		if (isNotNullOrUndefined(utilisateurDejaExistant)) {
			this.utilisateursSelectionnesAutocompleteAdmin.update(utilisateurs => [...utilisateurs, utilisateurDejaExistant]);
		}
		else {
			this.recupererEvenementsUtilisateurAutoCompleteAdmin(this.utilisateurAAjouter());
		}
		this.utilisateurAAjouter.set(null);
	}

	/**
	 * Désactive le bouton d'ajout d'utilisateur si aucun utilisateur n'est sélectionné.
	 */
	isButtonAjouterUtilisateurDisabled(): boolean {
		return this.utilisateurAAjouter() === null || this.utilisateurAAjouter() === undefined;
	}

	/**
	 * Vérifie si l'utilisateur connecté est administrateur pour lui afficher l'autocomplete.
	 */
	estAdministrateur(): boolean {
		return isNotNullOrUndefined(this.utilisateurConnecte.roles) ? this.utilisateurConnecte.droits?.ADMINISTRATION : false;
	}

	onRemoveFiltreUtilisateur(utilisateur: UtilisateurDto): void {
		this.utilisateursSelectionnesAutocompleteAdmin.update(utilisateurs => utilisateurs.filter(utilisateurSelectionne => utilisateurSelectionne.login !== utilisateur.login));
	}

	protected readonly isNotNullOrUndefined = isNotNullOrUndefined;
	protected readonly TypeEvenement = TypeEvenement;
	protected readonly TypeChangementDate = TypeChangementDate;
}
